import OpenSeadragon from '../../../openseadragon';
import { h, Component } from '../../../preact';
// Store, Dispatcher 직접 import -> 컴포넌트 props로 받아서 사용 (뷰어별 인스턴스 분리)
import leaveCanvas from '../actions/leaveCanvas';
import move from '../actions/move';
import press from '../actions/press';
import release from '../actions/release';
import { convertWidth, convertHeight } from '../utils/convert';
import { getArrowHeadGeometry } from '../utils/arrowHead';

const svgProperties = {
  xmlns: 'http://www.w3.org/2000/svg',
  version: '1.1',
  preserveAspectRatio: 'none',
  viewBox: '0 0 100 100',
  width: '100%',
  height: '100%',
};

function isVectorEffectSupported() {
  return document.documentElement.style.vectorEffect !== undefined;
}

const svgStyles = {
  cursor: 'default',
  'background-color': 'rgba(0,0,0,0)',
};

function getPathBoundingBox(d) {
  const nums = d.match(/-?\d+(\.\d+)?/g);
  if (!nums || nums.length < 2) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  const points = [];
  for (let i = 0; i < nums.length; i += 2) {
    const x = parseFloat(nums[i]);
    const y = parseFloat(nums[i + 1]);
    points.push({ x, y });
  }

  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;

  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

let _measureCanvas = null;
function measureTextWidth(text, fontSizePx) {
  if (!_measureCanvas) {
    _measureCanvas = document.createElement('canvas');
  }
  const ctx = _measureCanvas.getContext('2d');
  ctx.font = `400 ${fontSizePx}px Arial, sans-serif`;
  return ctx.measureText(text).width;
}

function textWidthToSvg(text, fontSize, scaleX) {
  const px = measureTextWidth(text, 100);
  return (px / 100) * fontSize * scaleX;
}

function wrapSvgText(text, labelX, paddingSvg, fontSize, scaleX, allowNewline = true) {
  if (!text) return [];

  const paragraphs = allowNewline ? text.split('\n') : [text.replace(/\n/g, ' ')];
  const lines = [];
  const maxBoxWidthSvg = Math.max((100 - labelX) - paddingSvg * 2, fontSize * scaleX * 2);

  for (const paragraph of paragraphs) {
    if (paragraph.length === 0) {
      lines.push('');
      continue;
    }

    let currentLine = '';
    for (const char of paragraph) {
      const testLine = currentLine + char;
      const testWidth = textWidthToSvg(testLine, fontSize, scaleX);
      if (testWidth > maxBoxWidthSvg && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = char;
      } else {
        currentLine = testLine;
      }
    }
    if (currentLine) lines.push(currentLine);
  }

  return lines.length > 0 ? lines : [''];
}

// 라벨 박스 크기/폰트 계산을 분리 (충돌 회피 사전 계산에서도 재사용).
function measureLabel(store, x, labelText) {
  const zoomLevel = store.getZoomLevel() || 1;
  const width = store.getWidth() || 1;
  const height = store.getHeight() || 1;
  const aspectRatio = width / height;

  const aspectNormalization = Math.sqrt(aspectRatio);

  // 사용자 지정 글씨 배율
  const labelScale = store.getLabelScale?.() ?? 1;
  const baseFontSize = 0.8 * labelScale;
  const minFontSize = 0.02;
  const maxFontSize = 1.8 * labelScale;
  const fontSize = Math.min(maxFontSize, Math.max(minFontSize, (baseFontSize / Math.pow(zoomLevel, 0.85)) * aspectNormalization));

  const paddingX = fontSize * 0.2;
  const paddingY = fontSize * 0.1;
  const lineHeight = fontSize * 1.15;

  const scaleX = aspectRatio > 1 ? (1 / aspectRatio) : 1;
  const scaleY = aspectRatio < 1 ? aspectRatio : 1;

  const lines = wrapSvgText(labelText, x, paddingX * scaleX, fontSize, scaleX);

  const effectiveScaleX = scaleX > 0 ? scaleX : 1;
  const maxLineWidth = Math.max(...lines.map(line =>
    textWidthToSvg(line, fontSize, scaleX) / effectiveScaleX
  ));

  const boxWidth = maxLineWidth + (paddingX * 2);
  const boxHeight = (lines.length * lineHeight) + (paddingY * 2);

  return { fontSize, paddingX, paddingY, lineHeight, scaleX, scaleY, lines, boxWidth, boxHeight };
}

function arrowLabelCenter(ax, ay, dir, boxWidth, boxHeight, gap, shift) {
  const s = shift || 0;
  return {
    cx: ax + dir.x * (boxWidth / 2 + gap + s),
    cy: ay + dir.y * (boxHeight / 2 + gap + s),
  };
}

const MAX_LABEL_SHIFT_RATIO = 3;

// store 인자 추가
// extraOffsetY: 라벨 충돌 회피용 추가 위쪽 이동량(로컬 단위, 화면상 위쪽으로 이동)
function createLabelNode(store, x, y, labelText, anchor = 'start', onClickHandler = null, annotationId = null, extraOffsetY = 0, dir = null) {
  if (!labelText) return null;

  const { fontSize, paddingX, paddingY, lineHeight, scaleX, scaleY, lines, boxWidth, boxHeight } = measureLabel(store, x, labelText);

  let offsetX = 0;
  let offsetY = -boxHeight - (extraOffsetY || 0);

  if (dir) {
    const baseGap = fontSize * 0.5; // computeLabelMeta 의 baseClearance 와 동일
    const { cx, cy } = arrowLabelCenter(0, 0, dir, boxWidth, boxHeight, baseGap, extraOffsetY || 0);
    offsetX = cx - boxWidth / 2;
    offsetY = cy - boxHeight / 2;
  } else if (anchor === 'end') {
    offsetX = -boxWidth;
  } else if (anchor === 'middle') {
    offsetX = -boxWidth / 2;
  } else {
    offsetX = 0;
  }

  const textStartX = offsetX + paddingX;
  const textStartY = offsetY + paddingY + (fontSize * 0.9);
  const scaledLineHeight = lineHeight;

  const labelClickHandler = (e) => {
    if (onClickHandler && annotationId) {
      const syntheticEvent = {
        ...e,
        target: { ...e.target, id: annotationId },
        stopPropagation: () => e.stopPropagation(),
        preventDefault: () => e.preventDefault(),
        clientX: e.clientX,
        clientY: e.clientY,
      };
      onClickHandler(syntheticEvent);
    } else {
      e.stopPropagation();
      console.log('Annotation label clicked:', labelText);
    }
  };

  const rotation = store.getRotation?.() ?? 0;
  const rotationTransform = rotation ? ` rotate(${-rotation})` : '';

  // 비균일 종횡비 보정(scale)을 역회전(rotate) 바깥쪽에 적용해야
  // 중간 각도 회전에서도 라벨이 기울거나 찌그러지지 않고 똑바로 보인다.
  // (scale -> rotate 순서: 화면 좌표계에서 순수 균일 스케일로 매핑됨)
  return h('g', {
    transform: `translate(${x}, ${y}) scale(${scaleX}, ${scaleY})${rotationTransform}`,
    style: { pointerEvents: 'none' },
  },
    h('rect', {
      x: offsetX,
      y: offsetY,
      width: boxWidth,
      height: boxHeight,
      fill: 'rgba(0, 0, 0, 0.3)',
      rx: fontSize * 0.3,
      ry: fontSize * 0.3,
      style: { pointerEvents: 'auto', cursor: 'pointer' },
      onClick: labelClickHandler,
    }),
    h('text', {
      x: textStartX,
      y: textStartY,
      'text-anchor': 'start',
      'font-size': fontSize,
      'font-family': 'Arial, sans-serif',
      'font-weight': 400,
      fill: '#ffffff',
      style: { textShadow: '0px 0px 2px rgb(0,0,0)' },
    },
      lines.map((line, idx) =>
        h('tspan', {
          x: textStartX,
          dy: idx === 0 ? 0 : scaledLineHeight,
        }, line),
      ),
    ),
  );
}

function makeCreateAnnotations(store, labelOffsets = []) {
  let fn = (el, index) => {
    const [shape, props, ...children] = el;
    const extraOffsetY = labelOffsets[index] || 0;

    if (shape === 'g') {
      if (props.stroke && children.length > 0) {
        children.forEach((child) => {
          if (Array.isArray(child) && child[1]) {
            child[1].stroke = props.stroke;
            if (child[0] === 'path' && child[1].fill && child[1].fill !== 'none') {
              child[1].fill = props.stroke;
            }
          }
        });
      }

      const isArrowGroup = props.class && props.class.includes('arrow-group');
      if (isArrowGroup && children.length >= 2) {
        const lineEl = children[0];
        const pathEl = children[1];
        const arrowEndX = parseFloat(props.arrowEndX);
        const arrowEndY = parseFloat(props.arrowEndY);

        if (
          lineEl && lineEl[0] === 'line' &&
          pathEl && pathEl[0] === 'path' &&
          !isNaN(arrowEndX) && !isNaN(arrowEndY)
        ) {
          const x1 = parseFloat(lineEl[1].x1);
          const y1 = parseFloat(lineEl[1].y1);

          const zoomLevel = store.getZoomLevel() || 1;
          const width = store.getWidth() || 1;
          const height = store.getHeight() || 1;
          const aspectRatio = width / height;

          if ((arrowEndX - x1) !== 0 || (arrowEndY - y1) !== 0) {
            const arrowHead = getArrowHeadGeometry({
              x1,
              y1,
              x2: arrowEndX,
              y2: arrowEndY,
              zoomLevel,
              aspectRatio,
            });

            lineEl[1].x2 = arrowHead.lineEndX.toString();
            lineEl[1].y2 = arrowHead.lineEndY.toString();
            pathEl[1].d = arrowHead.pathD;
          }
        }
      }

      const groupNode = h(shape, props, ...children.map((child) => {
        if (Array.isArray(child)) return h(child[0], child[1]);
        return child;
      }));

      if (!props.label) return groupNode;

      const info = getShapeLabelInfo(store, el);
      if (info) {
        const labelNode = createLabelNode(store, info.ux, info.uy, props.label, info.anchor, props.onClick, props.id, extraOffsetY, info.dir);
        return h('g', null, groupNode, labelNode);
      }

      return groupNode;
    }

    const shapeNode = h(shape, props);

    if (!props.label) return shapeNode;

    const info = getShapeLabelInfo(store, el);
    if (!info) return shapeNode;

    const labelNode = createLabelNode(store, info.ux, info.uy, props.label, info.anchor, props.onClick, props.id, extraOffsetY, info.dir);

    return h('g', null, shapeNode, labelNode);
  };

  if (!isVectorEffectSupported()) {
    fn = (el) => {
      const newEl = el;
      newEl[1]['stroke-width'] = convertWidth.toPercent(3);
      return h(...newEl);
    };
  }

  return fn;
}

// 로컬(화면 정렬) 좌표 (lx,ly)를 이미지 user 좌표로 변환.
// move.js의 localToUser와 동일 규칙(transform: translate scale rotate(-θ)).
function localToUserPoint(lx, ly, scaleX, scaleY, thetaDeg, ox, oy) {
  const rad = (thetaDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const rx = lx * cos + ly * sin;
  const ry = -lx * sin + ly * cos;
  return { x: ox + scaleX * rx, y: oy + scaleY * ry };
}

// 도형 외곽선을 user 좌표(0~100) 점들로 샘플링한다. 모든 도형 타입을 동일하게 처리.
function getOutlineSamples(el, scaleX, scaleY) {
  const [shape, props] = el;
  const pts = [];
  const push = (x, y) => {
    if (!isNaN(x) && !isNaN(y)) pts.push({ x, y });
  };
  const transformed = props.transform && props.drawRotation !== undefined;
  const ox = transformed ? parseFloat(props.originX) : 0;
  const oy = transformed ? parseFloat(props.originY) : 0;
  const td = transformed ? parseFloat(props.drawRotation) : 0;
  const add = (x, y) => {
    if (transformed) {
      const u = localToUserPoint(x, y, scaleX, scaleY, td, ox, oy);
      push(u.x, u.y);
    } else {
      push(x, y);
    }
  };

  if (shape === 'rect') {
    const x = parseFloat(props.x);
    const y = parseFloat(props.y);
    const w = parseFloat(props.width);
    const h = parseFloat(props.height);
    add(x, y); add(x + w, y); add(x + w, y + h); add(x, y + h);
  } else if (shape === 'ellipse') {
    const cx = parseFloat(props.cx);
    const cy = parseFloat(props.cy);
    const rx = parseFloat(props.rx);
    const ry = parseFloat(props.ry);
    const N = 16;
    for (let i = 0; i < N; i += 1) {
      const a = (2 * Math.PI * i) / N;
      add(cx + rx * Math.cos(a), cy + ry * Math.sin(a));
    }
  } else if (shape === 'path') {
    const nums = (props.d || '').match(/-?\d+(\.\d+)?/g);
    if (nums) {
      for (let i = 0; i + 1 < nums.length; i += 2) {
        push(parseFloat(nums[i]), parseFloat(nums[i + 1]));
      }
    }
  } else if (shape === 'line') {
    push(parseFloat(props.x1), parseFloat(props.y1));
    push(parseFloat(props.x2), parseFloat(props.y2));
  } else if (shape === 'g') {
    const lineEl = el[2];
    if (lineEl && lineEl[0] === 'line') {
      push(parseFloat(lineEl[1].x1), parseFloat(lineEl[1].y1));
    }
    push(parseFloat(props.arrowEndX), parseFloat(props.arrowEndY));
  }

  return pts;
}

// 화면 정렬(T) 프레임으로의 변환. user 좌표 -> child-local(화면 균일 스케일) 좌표.
// 라벨 그룹 transform(translate scale rotate(-θ))의 회전/스케일 역변환과 동일.
// 이 프레임에서 모든 라벨 박스는 화면상 수평/수직으로 정렬되고, 거리는 화면에 비례한다.
function toScreenFrame(ux, uy, scaleX, scaleY, cos, sin) {
  const vx = ux / scaleX;
  const vy = uy / scaleY;
  return { x: cos * vx - sin * vy, y: sin * vx + cos * vy };
}

// 라벨 앵커(user 좌표)를 계산한다.
// - 화살표(g): 회전과 무관하게 항상 시작점(꼬리)을 앵커로 삼아 헤드/타겟을 가리지 않음.
// - 그 외 도형: "화면상 가장 위쪽 외곽선 점"을 앵커로 삼아 라벨을 그 위로 띄운다(회전/줌 무관).
function getShapeLabelInfo(store, el) {
  if (!Array.isArray(el)) return null;
  const [shape, props] = el;
  if (!props || !props.label) return null;

  const width = store.getWidth() || 1;
  const height = store.getHeight() || 1;
  const aspectRatio = width / height;
  const scaleX = aspectRatio > 1 ? 1 / aspectRatio : 1;
  const scaleY = aspectRatio < 1 ? aspectRatio : 1;
  const rad = ((store.getRotation?.() ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  // 화살표(g)는 회전 상태와 무관하게 항상 시작점(꼬리)에 라벨을 둔다.
  // 앵커는 시작점, 배치 방향(dir)은 "화면상 헤드→꼬리"(=화살표 반대 방향)로 잡아
  // 라벨을 꼬리 바깥으로 밀어 화살표 본체/헤드/타겟을 전혀 가리지 않게 한다.
  if (shape === 'g') {
    const lineEl = el[2];
    if (lineEl && lineEl[0] === 'line') {
      const sx = parseFloat(lineEl[1].x1);
      const sy = parseFloat(lineEl[1].y1);
      const ex = parseFloat(props.arrowEndX);
      const ey = parseFloat(props.arrowEndY);
      if (!isNaN(sx) && !isNaN(sy)) {
        // 화면 정렬 프레임에서 헤드→꼬리 방향(라벨을 밀 방향). 기본은 위쪽.
        let dir = { x: 0, y: -1 };
        if (!isNaN(ex) && !isNaN(ey)) {
          const ss = toScreenFrame(sx, sy, scaleX, scaleY, cos, sin);
          const es = toScreenFrame(ex, ey, scaleX, scaleY, cos, sin);
          const vx = ss.x - es.x;
          const vy = ss.y - es.y;
          const len = Math.hypot(vx, vy);
          if (len > 1e-6) dir = { x: vx / len, y: vy / len };
        }
        return { ux: sx, uy: sy, anchor: 'middle', dir };
      }
    }
  }

  const pts = getOutlineSamples(el, scaleX, scaleY);
  if (!pts.length) return null;

  // 화면 정렬 프레임에서 y가 가장 작은(=화면상 가장 위) 점을 찾는다.
  let best = null;
  let bestY = Infinity;
  for (let i = 0; i < pts.length; i += 1) {
    const ty = sin * (pts[i].x / scaleX) + cos * (pts[i].y / scaleY);
    if (ty < bestY) {
      bestY = ty;
      best = pts[i];
    }
  }
  if (!best) return null;

  return { ux: best.x, uy: best.y, anchor: 'middle' };
}

// 한 어노테이션의 라벨 앵커/박스를 계산한다. 충돌 회피 사전 계산용.
// 좌표/거리는 모두 화면 정렬(T) 프레임의 child-local 단위로 통일한다.
function computeLabelMeta(store, el) {
  const info = getShapeLabelInfo(store, el);
  if (!info) return null;
  const props = el[1];
  const m = measureLabel(store, info.ux, props.label);

  const rad = ((store.getRotation?.() ?? 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const a = toScreenFrame(info.ux, info.uy, m.scaleX, m.scaleY, cos, sin);

  // 라벨을 외곽선 점 위로 살짝 띄우는 작은 간격(라벨 크기 비례 -> 화면상 일정).
  const gap = m.fontSize * 0.5;
  // dir 이 있으면(화살표) 그 방향으로 배치/회피, 없으면 기존 "위쪽" 규칙.
  return { a, anchor: info.anchor, m, baseClearance: gap, dir: info.dir || null };
}

// 라벨 박스끼리 겹치지 않도록 위쪽으로 밀어내는 오프셋(child-local 단위)을
// 어노테이션 index별로 계산한다. 모든 계산은 화면 정렬(T) 프레임에서 수행하므로
// 회전/줌과 무관하게 단위가 일관된다.
function resolveLabelCollisions(metas) {
  const offsets = new Array(metas.length).fill(0);
  const placed = []; // {left, right, top, bottom} (화면 정렬 프레임)
  const GAP = 0;

  for (let i = 0; i < metas.length; i += 1) {
    const meta = metas[i];
    if (!meta) continue;

    const { a, anchor, m, baseClearance, dir } = meta;
    const bw = m.boxWidth;
    const bh = m.boxHeight;

    // 화살표(dir 존재): 시작점에서 dir(헤드 반대) 방향으로 배치하고,
    // 겹치면 같은 방향으로 더 밀어 회피한다. createLabelNode 와 동일한 배치식.
    if (dir) {
      const makeBox = (s) => {
        const { cx, cy } = arrowLabelCenter(a.x, a.y, dir, bw, bh, baseClearance, s);
        return { left: cx - bw / 2, right: cx + bw / 2, top: cy - bh / 2, bottom: cy + bh / 2 };
      };
      const maxShift = bh * MAX_LABEL_SHIFT_RATIO;
      let shift = 0;
      let box = makeBox(0);
      let changed = true;
      let iterations = 0;
      while (changed && iterations < 200) {
        changed = false;
        iterations += 1;
        for (let p = 0; p < placed.length; p += 1) {
          const b = placed[p];
          const overlapX = box.left < b.right && box.right > b.left;
          const overlapY = box.top < b.bottom && box.bottom > b.top;
          if (overlapX && overlapY) {
            const next = shift + bh * 0.6 + GAP;
            if (next > maxShift) {
              // 더 밀지 않고 현재 위치를 유지 (약간 겹치더라도 화살표 옆에 붙여 둔다)
              changed = false;
              break;
            }
            shift = next;
            box = makeBox(shift);
            changed = true;
            break;
          }
        }
      }
      placed.push(box);
      offsets[i] = shift; // createLabelNode 에서 dir 방향 추가 이동량으로 사용
      continue;
    }

    let left;
    if (anchor === 'middle') left = a.x - bw / 2;
    else if (anchor === 'end') left = a.x - bw;
    else left = a.x;
    const right = left + bw;

    // 화면상 위쪽(=y 감소)으로 baseClearance + boxHeight 만큼 띄운 위치가 기본.
    const baseBottom = a.y - baseClearance;
    const baseTop = baseBottom - bh;

    let shift = 0;
    let changed = true;
    let iterations = 0;
    while (changed && iterations < 200) {
      changed = false;
      iterations += 1;
      const curTop = baseTop - shift;
      const curBottom = baseBottom - shift;
      for (let p = 0; p < placed.length; p += 1) {
        const box = placed[p];
        const overlapX = left < box.right && right > box.left;
        const overlapY = curTop < box.bottom && curBottom > box.top;
        if (overlapX && overlapY) {
          shift += (curBottom - (box.top - GAP));
          changed = true;
          break;
        }
      }
    }

    placed.push({ left, right, top: baseTop - shift, bottom: baseBottom - shift });
    // createLabelNode의 extraOffsetY(child-local)와 동일 단위.
    offsets[i] = baseClearance + shift;
  }

  return offsets;
}

class Annotations extends Component {
  getInitialState() {
    return { annotations: this.props.store.getAll() };
  }

  componentDidMount() {
    this.onChange = () => {
      this.setState({ annotations: this.props.store.getAll() });
    };
    this.props.store.addHandler('CHANGE_EVENT', this.onChange);
  }

  componentWillUnmount() {
    if (this.props.store && this.onChange) {
      this.props.store.removeHandler?.('CHANGE_EVENT', this.onChange);
      this.onChange = null;
    }
  }

  handleMouseLeave(e) {
    const { store, dispatcher } = this.props;
    if (store.notInMoveMode() && store.getMode() !== 'DRAWARROW') {
      e.stopPropagation();
      leaveCanvas(dispatcher, store);
    }
  }

  handleMouseUp(e) {
    const { store, dispatcher } = this.props;
    if (store.notInMoveMode()) {
      e.stopPropagation();
      release(dispatcher, store);
    }
  }

  coords(e) {
    const viewer = this.props && this.props.viewer;
    if (viewer && viewer.viewport && viewer.source && viewer.canvas) {
      const canvasRect = viewer.canvas.getBoundingClientRect();
      const pixel = new OpenSeadragon.Point(
        e.clientX - canvasRect.left,
        e.clientY - canvasRect.top,
      );
      const viewportPoint = viewer.viewport.pointFromPixel(pixel, true);
      const imagePoint = viewer.viewport.viewportToImageCoordinates(viewportPoint);
      const dimensions = viewer.source.dimensions;

      const x = Math.max(0, Math.min(100, (imagePoint.x / dimensions.x) * 100));
      const y = Math.max(0, Math.min(100, (imagePoint.y / dimensions.y) * 100));

      return [
        Math.round(x * 1000000) / 1000000,
        Math.round(y * 1000000) / 1000000,
      ];
    }

    const rect = this.base.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const x = 100 * offsetX / rect.width;
    const y = 100 * offsetY / rect.height;
    return [
      Math.round(x * 1000000) / 1000000,
      Math.round(y * 1000000) / 1000000,
    ];
  }

  handleMouseDown(e) {
    const { store, dispatcher } = this.props;
    if (store.notInMoveMode() && e.button === 0) {
      e.stopPropagation();
      press(...this.coords(e), dispatcher, store);
    }
  }

  handleMouseMove(e) {
    const { store, dispatcher } = this.props;
    if (store.notInMoveMode()) {
      e.stopPropagation();
      move(...this.coords(e), dispatcher, store);
    }
  }

  render() {
    const store = this.props.store;
    const annotations = this.state.annotations;

    // 라벨 충돌 회피: 먼저 모든 라벨 박스를 측정해 겹침을 해소할 오프셋을 구한다.
    const metas = annotations.map((el) => computeLabelMeta(store, el));
    const labelOffsets = resolveLabelCollisions(metas);

    const createAnnotations = makeCreateAnnotations(store, labelOffsets);

    return h(
      'svg',
      {
        ...svgProperties,
        style: svgStyles,
        onMouseDown: this.handleMouseDown.bind(this),
        onPointerDown: this.handleMouseDown.bind(this),
        onMouseLeave: this.handleMouseLeave.bind(this),
        onMouseMove: this.handleMouseMove.bind(this),
        onMouseUp: this.handleMouseUp.bind(this),
        onPointerUp: this.handleMouseUp.bind(this),
      },
      annotations.map((el, index) => createAnnotations(el, index)),
    );
  }
}

export default Annotations;
